import _ from 'lodash';

import { ARRAY_FUNCTIONS_WITH_ITERATORS, LEX_TOKEN_TYPES } from 'constants/parser';

import { UNQUOTED_TYPES } from './visualBuilderModel.constants';

/**
 * Determine whether a value node's typeOfValue requires quoting on emit.
 *
 * @param {string|null|undefined} typeOfValue
 * @returns {boolean}
 */
export const needsQuotes = typeOfValue => {
  if (_.isNil(typeOfValue)) {
    return false;
  }

  return !UNQUOTED_TYPES.has(typeOfValue);
};

/**
 * A spec_field is a member access on a function-call result
 * (e.g. `MIN_BY(WHERE(...), x).CreatedDate`). The parser rewrites it into a
 * field-typed node whose `value` holds a synthetic resolved type-path and whose
 * `operands` retain the original function call. It is never a plain field and
 * cannot be modelled, so it must round-trip via its reconstructed source text.
 *
 * @param {Object|null} node
 * @returns {boolean}
 */
export const isSpecField = node =>
  node?.type === LEX_TOKEN_TYPES.field && node?.spec_field === true;

/**
 * Reconstruct the original source text of a spec_field node from its retained
 * function operand plus the accessed field suffix.
 *
 * @param {Object} node
 * @returns {string}
 */
export const emitSpecFieldText = node => {
  const fnText = astToText(node.operands?.[0]);
  const suffix = _.last(String(node.value ?? '').split('.'));

  return suffix ? `${fnText}.${suffix}` : fnText;
};

/**
 * Emit one AST node back to expression text.
 * This is the minimal fallback used for unsupported subtrees (raw nodes).
 *
 * @param {Object|null} node
 * @returns {string}
 */
export const astToText = node => {
  if (_.isNil(node)) {
    return '';
  }

  const { type, value, typeOfValue, operands = [] } = node;

  if (isSpecField(node)) {
    return emitSpecFieldText(node);
  }

  if (type === LEX_TOKEN_TYPES.field) {
    return value;
  }

  if (type === LEX_TOKEN_TYPES.value) {
    if (typeOfValue === 'iterator') {
      // Skip synthetic iterator operands — caller must filter these
      return '';
    }

    return needsQuotes(typeOfValue) ? `'${value}'` : String(value);
  }

  if (type === LEX_TOKEN_TYPES.operator) {
    const lhsText = astToText(operands[0]);
    const rhsText = astToText(operands[1]);

    return `${lhsText} ${value} ${rhsText}`;
  }

  if (type === LEX_TOKEN_TYPES.function) {
    const fnName = value;
    const isArrayFn = ARRAY_FUNCTIONS_WITH_ITERATORS.includes(fnName);

    // Skip the synthetic iterator operand (index 1) for array functions
    const emitOperands = isArrayFn
      ? operands.filter(op => op?.typeOfValue !== 'iterator')
      : operands;

    const argTexts = emitOperands.map(astToText).filter(t => t !== '');

    return `${fnName}(${argTexts.join(', ')})`;
  }

  // date / bool / decimal literal nodes (LEX_TOKEN_TYPES.date, .bool, .decimal)
  return needsQuotes(typeOfValue) ? `'${value}'` : String(value ?? '');
};
